import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { SERVICES, CONTACT } from '../../data/siteData';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Industries', to: '/#industries' },
  { label: 'Success Stories', to: '/#success-stories' },
  { label: 'About Us', to: '/#about' },
  { label: 'FAQ', to: '/#faq' },
  { label: 'Contact', to: '/#contact' },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-[0_2px_20px_rgba(10,25,47,0.06)]' : 'bg-white/60 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-[72px]">
          <Link to="/" data-testid="header-logo" className="flex items-center">
            <span className="text-2xl md:text-[27px] font-extrabold tracking-tight font-heading">
              <span className="text-[#0A192F]">Work</span>
              <span className="text-[#0066CC]">Lynx</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <Link to="/" data-testid="nav-home" className="text-sm font-medium text-slate-700 hover:text-[#0066CC] transition-colors">Home</Link>
            <div className="relative group">
              <button data-testid="nav-services-dropdown" className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-[#0066CC] transition-colors py-6">
                Services <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-2 group-hover:translate-y-0 transition-all duration-200">
                <div className="w-[560px] bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10 p-4 grid grid-cols-2 gap-1">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group/item"
                    >
                      <div className="w-9 h-9 rounded-md bg-[#0A192F] flex items-center justify-center shrink-0">
                        <s.icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#0A192F] group-hover/item:text-[#0066CC] transition-colors">{s.name}</div>
                        <div className="text-xs text-slate-500 leading-snug mt-0.5 line-clamp-2">{s.short}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {NAV_LINKS.slice(1).map((l) => (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm font-medium text-slate-700 hover:text-[#0066CC] transition-colors">
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="header-phone" className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#0066CC] transition-colors">
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
            <Button asChild data-testid="header-cta-request-call" className="bg-[#0A192F] hover:bg-[#0F2A4A] text-white rounded-md px-5">
              <Link to="/#contact">Request a Call</Link>
            </Button>
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-[#0A192F]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" className="block py-2.5 text-sm font-medium text-slate-700">Home</Link>
            <button
              data-testid="mobile-services-toggle"
              className="flex w-full items-center justify-between py-2.5 text-sm font-medium text-slate-700"
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="pl-4 space-y-1 border-l-2 border-slate-100">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`mobile-nav-service-${s.slug}`} className="block py-2 text-sm text-slate-600">{s.name}</Link>
                ))}
              </div>
            )}
            {NAV_LINKS.slice(1).map((l) => (
              <Link key={l.label} to={l.to} className="block py-2.5 text-sm font-medium text-slate-700">{l.label}</Link>
            ))}
            <Button asChild className="w-full bg-[#0A192F] text-white mt-3">
              <Link to="/#contact">Request a Call</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
